/**
 * Fixed-capacity ring of recent positions for the star's trail. Pure data, no three.js.
 * A point is only recorded once the star has moved `minStep` from the newest one, so the trail is
 * evenly spaced along the path however fast the star is going.
 */
export class TrailBuffer {
  /** xyz triples; unused slots are zero */
  readonly positions: Float32Array;
  /** slot of the newest point */
  head = 0;
  /** how many slots hold real points */
  count = 0;
  private lx = 0;
  private ly = 0;
  private lz = 0;

  constructor(readonly capacity = 200, readonly minStep = 0.1) {
    this.positions = new Float32Array(capacity * 3);
  }

  reset() {
    this.head = 0;
    this.count = 0;
    this.positions.fill(0);
  }

  /** Record a position if it is far enough from the newest point. Returns true when it was recorded. */
  push(x: number, y: number, z = 0): boolean {
    if (this.count > 0) {
      const dx = x - this.lx, dy = y - this.ly, dz = z - this.lz;
      if (dx * dx + dy * dy + dz * dz < this.minStep * this.minStep) return false;
      this.head = (this.head + 1) % this.capacity;
    } else {
      this.head = 0;
    }
    const i = this.head * 3;
    this.positions[i] = x; this.positions[i + 1] = y; this.positions[i + 2] = z;
    this.lx = x; this.ly = y; this.lz = z;
    if (this.count < this.capacity) this.count++;
    return true;
  }

  /** How many steps older than the newest point slot i is, or -1 when the slot is unused. */
  ageOf(slot: number): number {
    const k = (this.head - slot + this.capacity) % this.capacity;
    return k < this.count ? k : -1;
  }

  /** The recorded points, newest first. */
  newestFirst(): [number, number, number][] {
    const out: [number, number, number][] = [];
    for (let k = 0; k < this.count; k++) {
      const i = ((this.head - k + this.capacity) % this.capacity) * 3;
      out.push([this.positions[i], this.positions[i + 1], this.positions[i + 2]]);
    }
    return out;
  }
}
